import { FC, useEffect, useState } from "react";
import { useNavigate } from "react-router";
import DefaultImg from "../../../assets/img/default-image.svg";
import {
  DEFAULT_ITEMS_PER_PAGE,
  DEFAULT_SEARCH_INPUT_DEBOUNCE_DELAY,
  DEFAULT_SEARCH_INPUT_LENGTH,
} from "../../../constants/app";
import { STORAGE_KEYS } from "../../../constants/storageKeys";
import useDebouncedValue from "../../../hooks/useDebouncedValue";
import { AUTH_ROUTE } from "../../../pages/App/routes";
import {
  useAppDispatch as useDispatch,
  useAppSelector as useSelector,
} from "../../../store";
import {
  hideTransparentPreloaderAction,
  showTransparentPreloaderAction,
} from "../../../store/application/slice";
import {
  getAllCandidatesThunk,
  getCandidateDataForCentreThunk,
} from "../../../store/centers/thunks";
import {
  CandidatesSortField,
  ICandidate,
  SortOrder,
} from "../../../store/centers/types";
import { PaginationLink } from "../../../store/superadmin/types";
import { setSelectedCentreDetailsAction } from "../../../store/vacancy/slice";
import storage from "../../../utils/storage";
import { capitalizeName } from "../../../utils/stringUtils";
import CustomButton from "../../UI/Buttons/CustomButton";
import Block from "../../UI/Icons/Block";
import Chevron from "../../UI/Icons/Chevron";
import RightArrow from "../../UI/Icons/RightArrow";
import PopupModal from "../../UI/Popup";
import CandidateFilter from "./CandidateFilter";
import CandidateItem from "./CandidateItem";
import BlockCandidate from "./PopupModal/BlockCandidate";
import ViewProfile from "./PopupModal/ViewProfile";

const Candidates: FC = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const allCandidatesLists = useSelector(
    (state) => state.Centres.allCandidates
  );
  const allBlockedCandidatesLists = useSelector(
    (state) => state.Centres.allBlockedCandidates
  );
  const allPreferredCandidatesLists = useSelector(
    (state) => state.Centres.allPreferredCandidates
  );
  const selectedCentreDetails = useSelector(
    (state) => state.vacancy.selectedCentreDetails
  );
  const allCentersLists = useSelector((state) => state.Centres.allCenters);
  const isShowNotification = useSelector(
    (state) => state.app.isShowNotification
  );

  const [isOpen1, setisOpen1] = useState(true);
  const [isOpen2, setisOpen2] = useState(true);

  const [showBlockModal, setShowBlockModal] = useState<boolean>(false);
  const [showProfileModal, setShowProfileModal] = useState<boolean>(false);
  const [showCentreModal, setShowCentreModal] = useState<boolean>(false);

  const [searchInput, setSearchInput] = useState("");
  const [selectedKeyword, setSelectedKeyword] = useState<string>("");

  const [currentPreferredPage, setCurrentPreferredPage] = useState<number>(1);
  const [preferredCandidateTotalPages, setPreferredCandidateTotalPages] =
    useState<number>(1);
  const [
    preferredCandidatePaginationLinks,
    setPreferredCandidatePaginationLinks,
  ] = useState<PaginationLink[]>([]);
  const [preferredPerPage, setPreferredPerPage] = useState<number>(
    DEFAULT_ITEMS_PER_PAGE
  );

  const [currentApprovedPage, setCurrentApprovedPage] = useState<number>(1);
  const [approvedCandidateTotalPages, setApprovedCandidateTotalPages] =
    useState<number>(1);
  const [
    approvedCandidatePaginationLinks,
    setApprovedCandidatePaginationLinks,
  ] = useState<PaginationLink[]>([]);
  const [approvedPerPage, setApprovedPerPage] = useState<number>(
    DEFAULT_ITEMS_PER_PAGE
  );

  const [selectedPreferredSortBy, setSelectedPreferredSortBy] =
    useState<string>("first_name");
  const [selectedPreferredSortOrder, setSelectedPreferredSortOrder] =
    useState<SortOrder>("asc");

  const [selectedApprovedSortBy, setSelectedApprovedSortBy] =
    useState<string>("first_name");
  const [selectedApprovedSortOrder, setSelectedApprovedSortOrder] =
    useState<SortOrder>("asc");

  const [blockCandidateId, setBlockCandidateId] = useState<number>(0);
  const [blockCandidateName, setBlockCandidateName] = useState<string | null>(
    null
  );

  const [selectedPositionsLevels, setSelectedPositionsLevels] = useState<
    number[]
  >([]);

  const [selectWorkedAtPreviously, setSelectWorkedAtPreviously] = useState<boolean>(false);
  const [selectedDistancyAway, setSelectedDistancyAway] = useState<number>(10);
  const [selectedKm, setSelectedKm] = useState<number>(10);

  useEffect(() => {
    if (storage.get(STORAGE_KEYS.SELECTED_CENTRE_UUID) === null) {
      setShowCentreModal(!false);
    } else {
      setShowCentreModal(false);
    }
  }, []);

  const handleBlock = (id: number) => {
    setBlockCandidateId(id);
    const getBlockCandidateName = allCandidatesLists?.data?.find(
      (allCandidatesList: ICandidate) => allCandidatesList.id === id
    );
    setBlockCandidateName(
      capitalizeName(getBlockCandidateName?.first_name ?? "") + " " + capitalizeName(getBlockCandidateName?.last_name ?? "")
    );
    setShowBlockModal(!false);
  };

  const handleProfile = async (id: string) => {
    dispatch(showTransparentPreloaderAction());
    await dispatch(
      getCandidateDataForCentreThunk({ candidate_id: String(id), centre_id: storage.get(STORAGE_KEYS.SELECTED_CENTRE_ID) })
    );
    dispatch(hideTransparentPreloaderAction());
    setShowProfileModal(!false);
  };

  const onClickClose = () => {
    setShowBlockModal(false);
    setShowProfileModal(false);
    setShowCentreModal(false);
  };

  const redirectToBlockedCandidates = () => {
    navigate(AUTH_ROUTE.CLIENT_CANDIDATES_BLOCKED);
  };

  /* Add delay to input the km */
  const debouncedKmSearch = useDebouncedValue(
    selectedDistancyAway,
    DEFAULT_SEARCH_INPUT_DEBOUNCE_DELAY
  );

  useEffect(() => {
    setSelectedKm(debouncedKmSearch);
  }, [debouncedKmSearch]);
  /* end - delay to input the km */

  const getAllCandidates = async () => {
    dispatch(showTransparentPreloaderAction());
    await dispatch(
      getAllCandidatesThunk({
        search_from: "active",
        keyword: selectedKeyword,
        preferred_page: currentPreferredPage,
        approved_page: currentApprovedPage,
        blocked_page: 1,
        preferred_per_page: preferredPerPage,
        approved_per_page: approvedPerPage,
        blocked_per_page: 10,
        preferred_sort_by: selectedPreferredSortBy,
        preferred_sort_order: selectedPreferredSortOrder,
        approved_sort_by: selectedApprovedSortBy,
        approved_sort_order: selectedApprovedSortOrder,
        blocked_sort_by: "",
        blocked_sort_order: "",
        selected_positions_levels: selectedPositionsLevels,
        centre_uuid:
          selectedCentreDetails !== null
            ? selectedCentreDetails
            : storage.get(STORAGE_KEYS.SELECTED_CENTRE_UUID),
        worked_with_previously: selectWorkedAtPreviously,
        distance_away: selectedKm
      })
    );
    dispatch(hideTransparentPreloaderAction());
  };

  useEffect(() => {
    if (!isShowNotification) {
      getAllCandidates();
    }
  }, [
    selectedKeyword,
    currentPreferredPage,
    currentApprovedPage,
    selectedPreferredSortBy,
    selectedPreferredSortOrder,
    selectedApprovedSortBy,
    selectedApprovedSortOrder,
    preferredPerPage,
    approvedPerPage,
    isShowNotification,
    selectedPositionsLevels,
    selectedCentreDetails,
    selectWorkedAtPreviously,
    selectedKm
  ]);

  /**
   * Debounces the `selectedKeyword` state to avoid excessive API calls while the user is typing.
   * The debounced value is used to trigger the API call for fetching candidates.
   */
  const debouncedSearch = useDebouncedValue(
    searchInput,
    DEFAULT_SEARCH_INPUT_DEBOUNCE_DELAY
  );

  /**
   * Handles the change in the search input by updating the `selectedKeyword` state.
   * It also resets the current page to 1 if the search input is empty or exceeds the default length.
   *
   */
  useEffect(() => {
    if (
      debouncedSearch.length === 0 ||
      debouncedSearch.length > DEFAULT_SEARCH_INPUT_LENGTH
    ) {
      setSelectedKeyword(debouncedSearch); // Update the selected keyword state
      setCurrentApprovedPage(1); // Reset the current page to 1
      setCurrentPreferredPage(1); // Reset the current page to 1
    }
  }, [debouncedSearch]);

  /**
   * Updates the current page and items per page when the component mounts or when the `candidatesList` changes.
   */
  useEffect(() => {
    if (allCandidatesLists && allCandidatesLists?.total > 0) {
      setApprovedCandidateTotalPages(allCandidatesLists?.last_page || 1);
      setApprovedCandidatePaginationLinks(allCandidatesLists?.links);
    }

    if (allPreferredCandidatesLists && allPreferredCandidatesLists?.total > 0) {
      setPreferredCandidateTotalPages(
        allPreferredCandidatesLists?.last_page || 1
      );
      setPreferredCandidatePaginationLinks(allPreferredCandidatesLists?.links);
    }
  }, [allCandidatesLists, allPreferredCandidatesLists]);

  /**
   * Handles the change in the number of items per page by updating the `itemPerPageList` state.
   *
   * @param value - The numeric value representing the number of items per page.
   */
  const handlePageChange = (url: string | null) => {
    if (url?.includes("approved_page")) {
      const approved_page = new URL(url).searchParams.get("approved_page");
      setCurrentApprovedPage(Number(approved_page));
    } else if (url?.includes("preferred")) {
      const preferred_page = new URL(url).searchParams.get("preferred_page");
      setCurrentPreferredPage(Number(preferred_page));
    }
  };

  // Handle sorting logic
  const handleSortChange = (field: CandidatesSortField, type: string) => {
    if (type === "preferred") {
      if (selectedPreferredSortBy === field) {
        setSelectedPreferredSortOrder((prev) =>
          prev === "asc" ? "desc" : "asc"
        );
      } else {
        setSelectedPreferredSortBy(field);
        setSelectedPreferredSortOrder("asc");
      }
    } else if (type === "approved") {
      if (selectedApprovedSortBy === field) {
        setSelectedApprovedSortOrder((prev) =>
          prev === "asc" ? "desc" : "asc"
        );
      } else {
        setSelectedApprovedSortBy(field);
        setSelectedApprovedSortOrder("asc");
      }
    }
  };

  const handleSetSelectedCentre = async (allCentreDetails: any) => {
    await dispatch(setSelectedCentreDetailsAction(allCentreDetails));
    setShowCentreModal(false);
  };

  return (
    <>
      {allCentersLists.length > 1 && 
        <PopupModal
          visible={showCentreModal}
          showCloseBtn={false}
          onClickClose={onClickClose}
          customWidth={650}
        >
          <div className="d-flex flex-column gap-20">
            <div className="fs-22 fw-400 text-black">Select a Centre</div>
            {allCentersLists.map((allCentersList: any, index: number) => (
              <div
                key={index}
                className="border-top border-off-white p-15px d-flex align-items-center gap-20"
              >
                <div className="min-w-50px w-50px h-50px radius-50px overflow-hidden">
                  <img
                    alt="Candidate"
                    width="50px"
                    height="50px"
                    className="img-responsive rounded-circle object-fit-cover"
                    src={allCentersList.photo || DefaultImg}
                  />
                </div>
                <div className="flex-grow-1 text-break">
                  <div className="d-flex flex-column flex-lg-row align-items-lg-center justify-content-lg-between gap-10">
                    <div className="d-flex flex-column gap-3">
                      <div className="fs-16 fw-700 text-black">
                        {allCentersList.business_name}
                      </div>
                      <div className="fs-13 fw-400 text-black">
                        {allCentersList.address}
                      </div>
                    </div>
                    <CustomButton
                      variant={"primary"}
                      type="button"
                      className="btn-theme bg-teal text-black min-w-110px mobile-w-100"
                      onClick={() => handleSetSelectedCentre(allCentersList)}
                    >
                      View
                      <RightArrow color="#000000" />
                    </CustomButton>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </PopupModal>
      }

      <div className="d-flex flex-column gap-10">
        <CandidateFilter
          setSearchInput={setSearchInput}
          selectedPositionsLevels={selectedPositionsLevels}
          selectWorkedAtPreviously={selectWorkedAtPreviously}
          selectedDistancyAway={selectedDistancyAway}
          setSelectedPositionsLevels={setSelectedPositionsLevels}
          setSelectWorkedAtPreviously={setSelectWorkedAtPreviously}
          setSelectedDistancyAway={setSelectedDistancyAway}
        />
        {(selectedKeyword.length > 0 && selectedPositionsLevels.length > 0) ||
        selectedKeyword.length === 0 ? (
          allPreferredCandidatesLists &&
          allPreferredCandidatesLists?.total > 0 ? (
            <>
              <div className="bg-teal-light py-8px px-15px radius-8px fs-16 fw-700 text-black d-flex gap-10 align-items-center justify-content-between">
                <div
                  className="d-flex gap-8 align-items-center cursor-pointer"
                  onClick={() => setisOpen1(!isOpen1)}
                >
                  <Chevron isOpen={isOpen1} />
                  {allPreferredCandidatesLists?.total > 0
                    ? allPreferredCandidatesLists?.total
                    : 0}{" "}
                  Preferred Candidates
                </div>
              </div>
              {isOpen1 && (
                <div className="d-flex flex-column">
                  <CandidateItem
                    type="preferred"
                    selectedSortBy={selectedPreferredSortBy}
                    selectedSortOrder={selectedPreferredSortOrder}
                    handleSortChange={handleSortChange}
                    candidateDatas={allPreferredCandidatesLists}
                    handleBlock={handleBlock}
                    handleProfile={handleProfile}
                    currentPage={currentPreferredPage}
                    totalPages={preferredCandidateTotalPages}
                    paginationLinks={preferredCandidatePaginationLinks}
                    onPageChange={handlePageChange}
                    setPerPageList={setPreferredPerPage}
                    setCurrentPage={setCurrentPreferredPage}
                    getAllCandidates={getAllCandidates}
                  />
                </div>
              )}
            </>
          ) : (
            <div className="bg-off-white py-8px px-15px radius-8px fs-16 fw-700 text-black">
              0 Preferred Candidates
            </div>
          )
        ) : (
          ""
        )}
        {selectedKeyword.length === 0 ||
        (selectedKeyword.length > 0 && selectedPositionsLevels.length > 0) ? (
          <div className="bg-off-white py-8px px-15px radius-8px fs-16 fw-700 text-black d-flex flex-column flex-lg-row gap-10 align-items-lg-center justify-content-lg-between">
            <div
              className="d-flex gap-8 align-items-center cursor-pointer"
              onClick={() => setisOpen2(!isOpen2)}
            >
              <Chevron isOpen={isOpen1} />
              {allCandidatesLists && allCandidatesLists?.total > 0
                ? allCandidatesLists?.total
                : 0}{" "}
              Candidates
            </div>
            <div className="d-flex gap-8 align-items-center cursor-pointer">
              <Block width={20} height={20} />
              <div
                className="fs-13 fw-700 text-black text-decoration-underline"
                onClick={redirectToBlockedCandidates}
              >
                {allBlockedCandidatesLists &&
                allBlockedCandidatesLists?.total > 0
                  ? allBlockedCandidatesLists?.total
                  : 0}{" "}
                Blocked Candidates
              </div>
            </div>
          </div>
        ) : (
          ""
        )}
        {isOpen2 && (
          <div className="d-flex flex-column">
            <CandidateItem
              type="approved"
              selectedSortBy={selectedApprovedSortBy}
              selectedSortOrder={selectedApprovedSortOrder}
              handleSortChange={handleSortChange}
              candidateDatas={allCandidatesLists ? allCandidatesLists : null}
              handleBlock={handleBlock}
              handleProfile={handleProfile}
              currentPage={currentApprovedPage}
              totalPages={approvedCandidateTotalPages}
              paginationLinks={approvedCandidatePaginationLinks}
              onPageChange={handlePageChange}
              setPerPageList={setApprovedPerPage}
              setCurrentPage={setCurrentApprovedPage}
              getAllCandidates={getAllCandidates}
            />
          </div>
        )}
      </div>

      <BlockCandidate
        showBlockModal={showBlockModal}
        onClickClose={onClickClose}
        blockCandidateId={blockCandidateId}
        blockCandidateName={blockCandidateName}
        setShowBlockModal={setShowBlockModal}
      />

      <ViewProfile
        showProfileModal={showProfileModal}
        onClickClose={onClickClose}
      />
    </>
  );
};

export default Candidates;
